import {
  EventFormat,
  GetMatchesHistoryResponse,
  GetPlayerProfileResponse,
  GetRatingHistoryResponse,
  PayloadGetMatchesHistory,
  PayloadGetRatingHistory,
  PayloadInvitePlayer,
  PayloadSearchPlayer,
  SearchPlayerResponse,
} from '@/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import httpService from './httpService'

const playerPath = '/player/v1.0'
const searchPath = `${playerPath}/search`
const claimPath = `${playerPath}/claim`
const claimDetailPath = `${playerPath}/claim/detail`
const matchHistoryUnauthenticatedPath = 'match/history/unauthenticated'
const invitePlayer = `${playerPath}/invite`
const byDuprId = `/player/search/byDuprId`

export enum PlayerQueryKeys {
  GET_RATING_HISTORY = 'get_rating_history',
  GET_PLAYER_PROFILE = 'get_player_profile',
  GET_MATCHES_HISTORY = 'get_matches_history',
  SEARCH_PLAYERS = 'search_players',
  SEARCH_PLAYERS_ADD_TO_CLUB = 'search_players_add_to_club',
  SEARCH_PLAYERS_ADD_PARTNER = 'search_players_add_partner',
  GET_CLAIM_USERS = 'get_claim_users',
}

export const searchPlayer = async (payload: PayloadSearchPlayer) => {
  return await httpService.Post<SearchPlayerResponse>(searchPath, payload)
}

export const claimPlayer = async (payload: PayloadSearchPlayer) => {
  return await httpService.Post<SearchPlayerResponse>(claimPath, payload)
}

export const useGetRatingHistory = (
  userId: number | undefined,
  payload: PayloadGetRatingHistory,
  type: EventFormat,
  startDate: string,
  endDate: string
) => {
  return useQuery(
    [PlayerQueryKeys.GET_RATING_HISTORY, type, userId, startDate, endDate],
    async () => {
      const res = await httpService.Post<GetRatingHistoryResponse>(`${playerPath}/${userId}/rating-history`, payload)
      return res.result.ratingHistory
    },
    {
      enabled: !!userId,
    }
  )
}

export const useSearchPlayerAddToClub = (payload: PayloadSearchPlayer) => {
  return useQuery(
    [PlayerQueryKeys.SEARCH_PLAYERS_ADD_TO_CLUB, JSON.stringify(payload)],
    async ({ signal }) => {
      const res = await httpService.Post(searchPath, payload, { signal })
      return res as SearchPlayerResponse
    },
    {
      enabled: !!payload.clubId,
      cacheTime: 0,
    }
  )
}

export const useSearchPlayerQuery = (payload: PayloadSearchPlayer) => {
  return useQuery(
    [PlayerQueryKeys.SEARCH_PLAYERS, JSON.stringify(payload)],
    async ({ signal }) => {
      const res = await httpService.Post(searchPath, payload, { signal })
      return res as SearchPlayerResponse
    },
    {
      cacheTime: 0,
    }
  )
}

export const useSearchPlayerAddPartner = (payload: PayloadSearchPlayer, open: boolean) => {
  return useQuery(
    [PlayerQueryKeys.SEARCH_PLAYERS_ADD_PARTNER, JSON.stringify(payload), open],
    async ({ signal }) => {
      const res = await httpService.Post(searchPath, payload, { signal })
      return res as SearchPlayerResponse
    },
    {
      enabled: open,
      cacheTime: 0,
    }
  )
}

export const useSearchPlayer = () => {
  return useMutation([searchPath], async (payload: PayloadSearchPlayer) => {
    const res = await httpService.Post(searchPath, payload)
    return res as SearchPlayerResponse
  })
}

async function getInternalIdViaDuprId(duprId: string) {
  const res = await httpService.Post<{
    status: 'SUCCESS'
    results: Array<{ userId: string }>
    errors: Array<{ message: string }>
  }>(byDuprId, {
    duprId,
  })

  return res.results[0].userId
}

export const useGetPlayerProfile = (playerId: string | undefined, enabled = true) => {
  return useQuery(
    [PlayerQueryKeys.GET_PLAYER_PROFILE, playerId],
    async () => {
      const playerInternalId = playerId && playerId.length === 6 ? await getInternalIdViaDuprId(playerId) : playerId

      const res = playerInternalId
        ? await httpService.Get<GetPlayerProfileResponse>(`${playerPath}/${playerInternalId}`)
        : null

      return res?.result
    },
    {
      enabled: !!playerId && enabled,
    }
  )
}

export const useGetMatchesHistory = () => {
  return useMutation([], async (payload: PayloadGetMatchesHistory) => {
    const { userId, ...restPayload } = payload
    const res = await httpService.Post<GetMatchesHistoryResponse>(`${playerPath}/${userId}/history`, restPayload)
    return res.result
  })
}

export const useGetClaimUsers = (payload: { limit: number; offset: number; query: string }) => {
  return useQuery(
    [PlayerQueryKeys.GET_CLAIM_USERS, JSON.stringify(payload)],
    async ({ signal }) => {
      const res = await httpService.Post<SearchPlayerResponse>(claimPath, payload, {
        signal,
      })
      return res.result
    },
    {
      enabled: Boolean(payload.query),
      cacheTime: 0,
    }
  )
}

export const useClaimDetail = (playerId?: number) => {
  return useQuery(
    [claimDetailPath, playerId],
    async () => {
      const res = await httpService.Get<GetPlayerProfileResponse>(`${claimPath}/${playerId}`)
      return res.result
    },
    {
      enabled: !!playerId,
      staleTime: 5 * 60 * 1000,
    }
  )
}

export const useGetMatchHistoryUnauthenticated = (playerId?: number, limit?: number, offset?: number) => {
  return useQuery(
    [matchHistoryUnauthenticatedPath, playerId],
    async () => {
      const res = await httpService.Get<GetMatchesHistoryResponse>(
        `match/v1.0/history/unauthenticated/${playerId}?limit=${limit}&offset=${offset}`
      )
      return res.result
    },
    {
      enabled: !!playerId,
    }
  )
}

export const useInvitePlayer = () => {
  return useMutation([], async (payload: PayloadInvitePlayer) => {
    const res = await httpService.Post(invitePlayer, payload)
    return res as any
  })
}
